import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  Lock,
  ShieldCheck,
  Check,
  AlertCircle,
  KeyRound,
} from "lucide-react";

const API_RESET = "https://api.martechlabs.in/api/auth/reset-password";

const FONT_HREF =
  "https://api.fontshare.com/v2/css?f[]=general-sans@400,500,600,700&display=swap";

const injectFonts = () => {
  if (typeof document === "undefined" || document.getElementById("auth-fonts"))
    return;
  const pre = document.createElement("link");
  pre.rel = "preconnect";
  pre.href = "https://api.fontshare.com";
  pre.crossOrigin = "anonymous";
  document.head.appendChild(pre);
  const link = document.createElement("link");
  link.id = "auth-fonts";
  link.rel = "stylesheet";
  link.href = FONT_HREF;
  document.head.appendChild(link);
};
injectFonts();

const post = async (url, body) => {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    });
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    return { ok: res.ok, status: res.status, data, networkError: false };
  } catch (e) {
    return { ok: false, status: 0, data: {}, networkError: true };
  }
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one from the login page.");
  }, [token]);

  const handleReset = async () => {
    setError("");
    if (!token) {
      setError("This reset link is invalid. Request a new one from the login page.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    const res = await post(API_RESET, { token, new_password: password });
    setLoading(false);
    if (res.networkError || res.status === 0) {
      setError("Can't reach the server. Please try again shortly.");
      return;
    }
    if (!res.ok || res.data.success === false) {
      setError(res.data.error || "This reset link is invalid or has expired. Request a new one.");
      return;
    }
    setDone(true);
  };

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-white text-neutral-900"
      style={{
        "--font-display": "'General Sans', ui-sans-serif, system-ui, sans-serif",
        "--font-body": "'General Sans', ui-sans-serif, system-ui, sans-serif",
        fontFamily: "var(--font-body)",
      }}
    >
      <style>{`
        @keyframes authRise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .auth-rise { animation: none !important; }
        }
      `}</style>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(50% 48% at 12% 8%, rgba(124,92,255,0.12), transparent 60%)," +
            "radial-gradient(42% 42% at 96% 6%, rgba(236,72,153,0.07), transparent 58%)," +
            "radial-gradient(55% 55% at 90% 96%, rgba(99,102,241,0.09), transparent 62%)",
        }}
      />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-10 sm:px-8">
        <section
          className="auth-rise w-full max-w-[420px]"
          style={{ animation: "authRise 0.6s ease-out both" }}
        >
          <div className="mb-7 flex items-center justify-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/25">
              <ShieldCheck size={18} className="text-white" />
            </div>
            <span
              className="text-lg font-semibold text-neutral-900"
              style={{ fontFamily: "var(--font-display)", letterSpacing: "0.01em" }}
            >
              Martech&nbsp;CDP
            </span>
          </div>

          <div className="w-full rounded-2xl border border-neutral-200 bg-white p-7 sm:p-8 shadow-[0_24px_60px_-28px_rgba(80,40,160,0.30)]">
            {error && (
              <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}

            {done ? (
              <div className="py-2 text-center">
                <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-emerald-100">
                  <Check size={22} className="text-emerald-600" />
                </div>
                <h2
                  className="text-xl font-semibold text-neutral-900"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Password reset
                </h2>
                <p className="mt-2 text-sm text-neutral-500">
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 py-3 text-[15px] font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:brightness-110 active:scale-[0.99]"
                >
                  Go to sign in
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2
                    className="text-[1.4rem] font-semibold tracking-tight text-neutral-900"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Set a new password
                  </h2>
                  <p className="mt-1.5 text-sm text-neutral-500">
                    Choose a new password for your account.
                  </p>
                </div>
                <div className="space-y-3.5">
                  <div className="relative">
                    <div className="group relative">
                      <Lock
                        size={17}
                        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors group-focus-within:text-violet-500"
                      />
                      <input
                        type={showPwd ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="New password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleReset()}
                        className="w-full rounded-xl border border-neutral-200 bg-white py-3 pl-11 pr-11 text-[15px] text-neutral-900 placeholder-neutral-400 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors hover:text-neutral-700"
                        aria-label={showPwd ? "Hide password" : "Show password"}
                      >
                        {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>
                  <div className="group relative">
                    <Lock
                      size={17}
                      className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors group-focus-within:text-violet-500"
                    />
                    <input
                      type={showPwd ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Confirm new password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleReset()}
                      className="w-full rounded-xl border border-neutral-200 bg-white py-3 pl-11 pr-11 text-[15px] text-neutral-900 placeholder-neutral-400 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20"
                    />
                  </div>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleReset}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 py-3 text-[15px] font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <>Reset password <KeyRound size={17} /></>}
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
